import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import type { IResourceDatabaseView } from '@/api/types';
import type { ITableSortingState } from '@/components/common/table/types';
import type { IDatabaseViewColumnNames } from '@/components/common';

import {
  Table,
  TableSection,
  ResourceTableCell,
  ResourceTableHeader,
  ResourceTableHeaderCell,
  ResourceTableRow,
  ResourcesIcon,
  DropdownMenu,
  ClickableResourceTableCell,
} from '@/components/common';
import { breakpoints, Mobile, TabletAndDesktop } from '@/breakpoints-config/breakpoints';

interface IProps {
  items: IResourceDatabaseView[];
  sortingState: ITableSortingState<IDatabaseViewColumnNames>;
  onSort: (columnName: IDatabaseViewColumnNames) => void;
  className?: string;
  selectViewAction: (viewId: number) => void;
  exportViewAction: (viewId: number, viewName: string) => void;
}

const ResourceDatabaseViewsTable = ({
  items,
  sortingState,
  onSort,
  className,
  selectViewAction,
  exportViewAction,
}: IProps): ReactElement => {
  const { t } = useTranslation();
  const isTabletAndDesktop = useMediaQuery({
    minWidth: Number(breakpoints.small) + 1,
  });

  const itemsToShow = useMemo(
    () => (isTabletAndDesktop ? items : items.filter(item => !item.isDefault)),
    [isTabletAndDesktop, items],
  );

  const defaultItem = items.find(item => item.isDefault);

  return (
    <Table className={className}>
      <ResourceTableHeader className="small">
        <TabletAndDesktop>
          <ResourceTableHeaderCell width="26px" minWidth="26px" />
        </TabletAndDesktop>
        <ResourceTableHeaderCell
          size={2}
          sortingEnabled
          sortingState={sortingState.name}
          onClick={() => onSort('name')}
        >{t`resources.foldersList.headerName`}</ResourceTableHeaderCell>
        <ResourceTableHeaderCell
          size={2}
          sortingEnabled
          sortingState={sortingState.viewAccess}
          onClick={() => onSort('viewAccess')}
        >{t`resources.foldersList.headerViewAccess`}</ResourceTableHeaderCell>
        <TabletAndDesktop>
          <ResourceTableHeaderCell size={1} />
        </TabletAndDesktop>
        <ResourceTableHeaderCell width="24px" minWidth="24px" className="p-0 ms-3" />
      </ResourceTableHeader>
      {defaultItem && (
        <Mobile>
          <TableSection label={t`resources.databases.defaultView`}>
            <Rows items={[defaultItem]} selectViewAction={selectViewAction} exportViewAction={exportViewAction} />
          </TableSection>
        </Mobile>
      )}
      {isTabletAndDesktop || !itemsToShow.length ? (
        <Rows items={itemsToShow} selectViewAction={selectViewAction} exportViewAction={exportViewAction} />
      ) : (
        <TableSection label={t`resources.databases.allViews`}>
          <Rows items={itemsToShow} selectViewAction={selectViewAction} exportViewAction={exportViewAction} />
        </TableSection>
      )}
    </Table>
  );
};

export { ResourceDatabaseViewsTable };

const Rows = ({
  items,
  selectViewAction,
  exportViewAction,
}: {
  items: IProps['items'];
  selectViewAction: IProps['selectViewAction'];
  exportViewAction: IProps['exportViewAction'];
}): ReactElement => {
  const { t } = useTranslation();

  return (
    <>
      {items.map((item, id) => (
        <ResourceTableRow className="small" key={id}>
          <TabletAndDesktop>
            <ResourceTableCell width="26px" minWidth="26px" className="m-0">
              <ResourcesIcon data={item} />
            </ResourceTableCell>
          </TabletAndDesktop>
          <ClickableResourceTableCell size={2} onClick={() => selectViewAction(item.id)}>
            {item.name}
          </ClickableResourceTableCell>
          <ResourceTableCell size={2}>{t(`resources.databases.viewAccess.${item.viewAccess}`)}</ResourceTableCell>
          <TabletAndDesktop>
            <ResourceTableCell size={1}>{item.isDefault && t`resources.databases.defaultView`}</ResourceTableCell>
          </TabletAndDesktop>
          <ResourceTableCell width="24px" minWidth="24px" className="dropdown-action">
            <DropdownMenu
              options={[
                {
                  label: t`common.btnExport`,
                  callback: () => exportViewAction(item.id, item.name),
                },
              ]}
            />
          </ResourceTableCell>
        </ResourceTableRow>
      ))}
    </>
  );
};
